import React, { useState, useEffect, useContext, createContext } from 'react';
import { AuthState, AuthContextType, LoginCredentials, User } from '../types/authTypes';
import authService from '../services/authService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [auth, setAuth] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = authService.getToken();
        const user = authService.getUser();
        
        if (token && user) {
          setAuth({
            user: user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
        } else {
          setAuth(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        authService.clearAuth();
        setAuth({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: 'Failed to initialize authentication'
        });
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<User> => {
    try {
      setAuth(prev => ({ ...prev, isLoading: true, error: null }));
      const response = await authService.login(credentials);
      
      // Type guard: check if response is successful and has user data
      if (!response.status || !('user' in response.data)) {
        throw new Error('Login failed');
      }

      console.log("response.data::::::", response.data)
      
      const { token, user } = response.data;
      
      authService.setToken(token);
      authService.setUser(user);
      
      setAuth({
        user: user,
        token: token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
      
      return user;
    } catch (error: any) {
      setAuth(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Login failed',
      }));
      throw error;
    }
  };

  const logout = () => {
    authService.clearAuth();
    setAuth({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });
  };

  const clearError = () => {
    setAuth(prev => ({ ...prev, error: null }));
  };

  const value: AuthContextType = {
    auth,
    login,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};